import { httpCoreService } from "../services/HttpCoreService";
import type { ApiResponse } from "../types/auth.type";
import type {
  EnrollmentPlan,
  SiteEnrollmentRow,
  MonthlyForecast,
  UpdateStudyTargetRequest,
  UpdateSiteTargetRequest,
  CreateForecastRowRequest,
  UpdateForecastRowRequest,
  GenerateForecastRequest,
} from "@/pages/StudyManagement/components/enrollment/types";

function assertSuccess<T>(response: ApiResponse<T>, errorMsg: string): T {
  const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
  if (!isSuccess) {
    throw new Error(response.error ?? response.message ?? errorMsg);
  }
  if (response.data === undefined || response.data === null) {
    throw new Error(`${errorMsg}: No data returned from server.`);
  }
  return response.data;
}

// ─── Interface ────────────────────────────────────────────────────────────────

export interface IEnrollmentRepository {
  getEnrollmentPlan(studyId: string): Promise<EnrollmentPlan>;
  updateStudyTarget(
    studyId: string,
    req: UpdateStudyTargetRequest,
  ): Promise<{ studyTarget: number }>;
  getSiteEnrollments(studyId: string): Promise<SiteEnrollmentRow[]>;
  updateSiteTarget(
    studyId: string,
    siteId: string,
    req: UpdateSiteTargetRequest,
  ): Promise<SiteEnrollmentRow>;
  getForecast(
    studyId: string,
    page?: number,
    limit?: number,
  ): Promise<MonthlyForecast[]>;
  createForecastRow(
    studyId: string,
    req: CreateForecastRowRequest,
  ): Promise<MonthlyForecast>;
  updateForecastRow(
    studyId: string,
    rowId: string,
    req: UpdateForecastRowRequest,
  ): Promise<MonthlyForecast>;
  deleteForecastRow(studyId: string, rowId: string): Promise<void>;
  generateForecast(
    studyId: string,
    req: GenerateForecastRequest,
  ): Promise<MonthlyForecast[]>;
}

// ─── Implementation ───────────────────────────────────────────────────────────

export class EnrollmentRepository implements IEnrollmentRepository {
  async getEnrollmentPlan(studyId: string): Promise<EnrollmentPlan> {
    const res = await httpCoreService.get<EnrollmentPlan>(
      `/studies/${studyId}/enrollment`,
    );
    return assertSuccess(res, "Failed to fetch enrollment plan");
  }

  async updateStudyTarget(
    studyId: string,
    req: UpdateStudyTargetRequest,
  ): Promise<{ studyTarget: number }> {
    const res = await httpCoreService.put<{ studyTarget: number }>(
      `/studies/${studyId}/enrollment/target`,
      req,
    );
    return assertSuccess(res, "Failed to update study target");
  }

  async getSiteEnrollments(studyId: string): Promise<SiteEnrollmentRow[]> {
    const res = await httpCoreService.get<SiteEnrollmentRow[]>(
      `/studies/${studyId}/enrollment/sites`,
    );
    return assertSuccess(res, "Failed to fetch site enrollments");
  }

  async updateSiteTarget(
    studyId: string,
    siteId: string,
    req: UpdateSiteTargetRequest,
  ): Promise<SiteEnrollmentRow> {
    const res = await httpCoreService.put<SiteEnrollmentRow>(
      `/studies/${studyId}/enrollment/sites/${siteId}`,
      req,
    );
    return assertSuccess(res, "Failed to update site target");
  }

  async getForecast(
    studyId: string,
    page = 1,
    limit = 50,
  ): Promise<MonthlyForecast[]> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    const res = await httpCoreService.get<{ items: MonthlyForecast[] }>(
      `/studies/${studyId}/enrollment/forecast?${params.toString()}`,
    );
    const data = assertSuccess(res, "Failed to fetch forecast");
    return data.items ?? [];
  }

  async createForecastRow(
    studyId: string,
    req: CreateForecastRowRequest,
  ): Promise<MonthlyForecast> {
    const res = await httpCoreService.post<MonthlyForecast>(
      `/studies/${studyId}/enrollment/forecast`,
      req,
    );
    return assertSuccess(res, "Failed to create forecast row");
  }

  async updateForecastRow(
    studyId: string,
    rowId: string,
    req: UpdateForecastRowRequest,
  ): Promise<MonthlyForecast> {
    const res = await httpCoreService.put<MonthlyForecast>(
      `/studies/${studyId}/enrollment/forecast/${rowId}`,
      req,
    );
    return assertSuccess(res, "Failed to update forecast row");
  }

  async deleteForecastRow(studyId: string, rowId: string): Promise<void> {
    await httpCoreService.delete(
      `/studies/${studyId}/enrollment/forecast/${rowId}`,
    );
  }

  async generateForecast(
    studyId: string,
    req: GenerateForecastRequest,
  ): Promise<MonthlyForecast[]> {
    const res = await httpCoreService.post<{
      generated: number;
      forecast: MonthlyForecast[];
    }>(`/studies/${studyId}/enrollment/forecast/generate`, req);
    const data = assertSuccess(res, "Failed to auto-generate forecast");
    return data.forecast;
  }
}
